/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * PublishResultsPage — /organizer/results/:raceId
 *
 * Declare, then publish. Two stages on purpose: the draft is ours to get wrong
 * and change again, and nothing unpublished can leak because an unpublished
 * winner does not exist in the award table at all.
 */

import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import {
  fetchOrganizerRaceResults, fetchAwards, setDeclaredPosition, publishResults, unpublishResults,
  removeResult, fetchRacePublished,
} from '../feed/results/publishApi';
import { publishBlocker, declaredWinners } from '../feed/results/publishModel';
import { formatClock, formatKm } from '../feed/results/boardModel';
import { labelOf } from '../feed/results/resultsApi';
import { idOf } from '../feed/results/boardIndexModel';
import ImportResultsPanel from './ImportResultsPanel';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const HAIR = 'rgba(36, 23, 8, .12)';
const mono = { fontVariantNumeric: 'tabular-nums' };

/**
 * How many places the picker offers.
 *
 * Nothing below this line enforces three: publishBlocker only refuses the same
 * place twice, and the column's own constraint is `declared_position >= 1` with
 * no ceiling. The three options were the whole limit, so raising this number is
 * the whole change — a race awarding ten deep needs no migration.
 */
const PODIUM_PLACES = 10;

/** "1st", "2nd", "3rd", "4th" … */
function ordinal(n) {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
}

export default function PublishResultsPage() {
  const { raceId } = useParams();
  const race = Number(raceId);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  // Which row is mid-way through "Remove → Confirm". One at a time: a second
  // Remove elsewhere cancels the first, so there is never more than one armed
  // delete on screen.
  const [confirming, setConfirming] = useState(null);

  const results = useQuery({
    queryKey: ['organizer', 'results', race],
    queryFn: ({ signal }) => fetchOrganizerRaceResults(apiHelper, { eventActivityId: race, signal }),
    enabled: Number.isFinite(race),
  });
  const awards = useQuery({
    queryKey: ['organizer', 'awards', race],
    queryFn: ({ signal }) => fetchAwards(apiHelper, { eventActivityId: race, signal }),
    enabled: Number.isFinite(race),
  });
  // Whether anything is on the public podium RIGHT NOW. Award rows outlive an
  // Unpublish by design, so they cannot answer this on their own.
  const racePublished = useQuery({
    queryKey: ['organizer', 'race-published', race],
    queryFn: ({ signal }) => fetchRacePublished(apiHelper, { eventActivityId: race, signal }),
    enabled: Number.isFinite(race),
  });

  const rows = useMemo(
    () => [...(results.data?.rows || [])].sort(
      (a, b) => (Number(a.duration_seconds) || Infinity) - (Number(b.duration_seconds) || Infinity)),
    [results.data],
  );
  const winners = declaredWinners(rows);
  const blocker = publishBlocker(rows);
  // HOW MANY WINNERS ARE ON THE PUBLIC PODIUM RIGHT NOW — gated on the race's
  // flag, not on the award rows alone.
  //
  // Award rows outlive an Unpublish on purpose, so counting them said
  // "3 winners published" about a race whose podium was down, and left the
  // Unpublish button live with nothing to withdraw. Found 2026-11-03 while the
  // flag was false and three award rows were still present.
  const published = racePublished.data ? (awards.data?.rows || []).length : 0;

  /**
   * The results currently ON the public podium. Remove is refused for these,
   * and THIS GUARD IS THE ONLY THING STOPPING IT.
   *
   * The obvious assumption — "an award points at the result, so the database
   * will refuse the delete" — is wrong, and was measured to be wrong on
   * 2026-11-03: `event_result` is SOFT deleted, so `deleted_at` is set, the
   * foreign key is never tested, and the award is left pointing at a row the
   * board can no longer read. The podium then draws that winner with "Time not
   * available" forever. Nothing downstream catches it.
   *
   * ANY AWARD BLOCKS IT, published or not — and that is deliberate, after
   * getting it wrong in both directions on 2026-11-03.
   *
   * The first cut blocked only while the race was published, reasoning that an
   * unpublished podium harms nobody. But `unpublishResults` KEEPS the award
   * rows (deleting them is what once made republishing impossible), so
   * removing a former winner after an Unpublish left the award pointing at a
   * soft-deleted result. Measured: three such rows. Republishing happens to
   * repair them, because publishResults rewrites the award table — but until
   * someone does, the race carries awards that reference nothing.
   *
   * Deleting the stale award instead is NOT the answer: the award table's own
   * uniqueness is still a plain constraint that counts deleted rows, so a
   * removed award would block that placing from ever being awarded again.
   * That is the same trap `event_result` just escaped, and the award table has
   * not had the conditional-index treatment yet.
   *
   * The way to remove a declared winner is therefore: clear their place,
   * publish again so the award table no longer names them, then remove.
   *
   * `idOf`, NOT String(): the fetch API hands `event_result_id` back expanded
   * as an object. `String(a.event_result_id)` yields "[object Object]", the set
   * matches nothing, and every published winner is offered a Remove button —
   * which is exactly what happened the first time this ran.
   */
  const publishedResultIds = useMemo(
    () => new Set(
      (awards.data?.rows || [])
        .map((a) => idOf(a.event_result_id))
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    ),
    [awards.data],
  );

  /**
   * What the table draws. ONLY the table.
   *
   * Everything that decides whether this race can be published — the winners,
   * the blocker, the counts — reads `rows`, never this. A search is a way of
   * finding one athlete among two hundred, and it must never be able to change
   * what gets published by hiding somebody.
   */
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => (
      String(row.athlete_name || '').toLowerCase().includes(needle)
      || String(row.bib || '').toLowerCase().includes(needle)
    ));
  }, [rows, search]);

  async function mark(resultId, position) {
    setError(''); setNote('');
    setBusy(`mark-${resultId}`);
    try {
      await setDeclaredPosition(apiHelper, { resultId, position });
      await results.refetch();
    } catch (e) {
      setError(e.message || 'Could not mark that place.');
    } finally { setBusy(''); }
  }

  /**
   * Detach one result, so the athlete can be attached again.
   *
   * This is the answer to "I picked the wrong run". There is no re-point: the
   * result goes, the entrant returns to the attach panel with every run they
   * recorded that day, and the organizer chooses again. Nothing else has to
   * change for that to work — the panel lists entrants with no result yet, so
   * the athlete reappears on the next read, and the run that was wrongly taken
   * is free again because no result holds it any more.
   */
  async function onRemove(row) {
    setError(''); setNote('');
    setBusy(`remove-${row.id}`);
    try {
      await removeResult(apiHelper, { resultId: row.id });
      setConfirming(null);
      setNote(
        `${row.athlete_name || 'That athlete'} is back on the attach panel — `
        + 'pick the right run and attach it again.',
      );
      await results.refetch();
    } catch (e) {
      setError(e.message || 'Could not remove that result.');
    } finally { setBusy(''); }
  }

  async function onPublish() {
    setError(''); setNote('');
    setBusy('publish');
    try {
      const first = rows[0] || {};
      const written = await publishResults(apiHelper, {
        eventActivityId: race,
        activityName: labelOf(first.event_activity_id, `Race ${race}`),
        eventName: '',
        results: rows,
      });
      setNote(`Published ${written} winner${written === 1 ? '' : 's'}.`);
      // racePublished too: it is what gates Remove, and publishing has just
      // put these winners on the podium.
      await Promise.all([results.refetch(), awards.refetch(), racePublished.refetch()]);
    } catch (e) {
      setError(e.message || 'Could not publish.');
    } finally { setBusy(''); }
  }

  async function onUnpublish() {
    setError(''); setNote('');
    setBusy('unpublish');
    try {
      const withdrawn = await unpublishResults(apiHelper, { eventActivityId: race });
      setNote(`Withdrawn ${withdrawn} award${withdrawn === 1 ? '' : 's'}. Your picks are kept.`);
      // The flag has just gone false, which is what lets Remove come back.
      await Promise.all([awards.refetch(), racePublished.refetch()]);
    } catch (e) {
      setError(e.message || 'Could not unpublish.');
    } finally { setBusy(''); }
  }

  if (!Number.isFinite(race)) {
    return <p style={{ padding: 24, color: MUTED }}>That race link is not valid.</p>;
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '8px 4px 56px' }}>
      <Link to="/organizer/results" style={{ fontSize: 13, color: MUTED }}>← All races</Link>
      <h1 style={{ margin: '10px 0 6px', fontSize: 24, fontWeight: 700, color: INK }}>
        Declare and publish
      </h1>
      <p style={{ margin: '0 0 18px', fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
        {/* Reworded 2026-11-02, phase 7a. The old line read "Nothing is public
            until you press Publish", which was never true: a result is on the
            public board the moment it is attached, and only the PODIUM waits for
            this button. Settled as decision 2 of phase 7. */}
        Mark the places against the finishers. The results list is already public — publishing
        announces the winners, and publishing again replaces them rather than adding to them.
        {' '}
        <Link to={`/results/${race}`} style={{ color: ACCENT, fontWeight: 600 }}>View the public board</Link>
      </p>

      {/* Before the podium can be marked there have to be results to mark it
          against, so the import sits above the list rather than below it. */}
      <ImportResultsPanel
        api={apiHelper}
        eventActivityId={race}
        existingResults={rows}
        onImported={() => results.refetch()}
      />

      {error && <div role="alert" style={{ padding: '12px 14px', borderRadius: 10, background: '#FFF1E8', color: '#8A2C06', fontSize: 14, marginBottom: 14 }}>{error}</div>}
      {note && <div role="status" style={{ padding: '12px 14px', borderRadius: 10, background: '#ECFDF3', color: '#1B7F48', fontSize: 14, marginBottom: 14 }}>{note}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <button type="button" onClick={onPublish} disabled={!!blocker || busy === 'publish'}
          style={{ minHeight: 46, padding: '0 20px', border: 0, borderRadius: 11, background: blocker ? '#D8CEC3' : INK, color: '#fff', fontWeight: 700, fontSize: 14, cursor: blocker ? 'not-allowed' : 'pointer' }}>
          {busy === 'publish' ? 'Publishing…' : 'Publish results'}
        </button>
        <button type="button" onClick={onUnpublish} disabled={published === 0 || busy === 'unpublish'}
          style={{ minHeight: 46, padding: '0 20px', borderRadius: 11, border: `1px solid ${HAIR}`, background: '#fff', color: published === 0 ? '#A69683' : INK, fontWeight: 600, fontSize: 14, cursor: published === 0 ? 'not-allowed' : 'pointer' }}>
          {busy === 'unpublish' ? 'Withdrawing…' : 'Unpublish'}
        </button>
        <span style={{ fontSize: 13, color: MUTED }}>
          {published > 0 ? `${published} winner${published === 1 ? '' : 's'} published` : 'Nothing published yet'}
          {winners.length > 0 ? ` · ${winners.length} marked` : ''}
        </span>
      </div>

      {blocker && <p style={{ fontSize: 13, color: MUTED, marginTop: -8, marginBottom: 16 }}>{blocker}</p>}

      {results.isPending && <p style={{ fontSize: 14, color: MUTED }}>Loading results…</p>}
      {!results.isPending && rows.length === 0 && (
        <p style={{ fontSize: 14, color: MUTED }}>
          No results for this race yet. They appear as athletes add their runs.
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <label htmlFor="result-search" style={{ fontSize: 13, color: MUTED }}>
            Find an athlete
          </label>
          <input
            id="result-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or bib number"
            style={{
              minHeight: 40, flex: '1 1 260px', maxWidth: 360, padding: '0 12px',
              borderRadius: 9, border: `1px solid ${HAIR}`, fontSize: 14, color: INK,
            }}
          />
          <span style={{ fontSize: 13, color: MUTED }}>
            {search.trim()
              ? `${shown.length} of ${rows.length} shown`
              : `${rows.length} result${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
      )}

      {rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Athlete', 'Distance', 'Time', 'Status', 'Place', ''].map((h) => (
                <th key={h} scope="col" style={{ textAlign: h === 'Athlete' ? 'left' : 'right', padding: '10px 12px', fontSize: 11, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED, borderBottom: `1px solid ${HAIR}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '18px 12px', fontSize: 14, color: MUTED }}>
                  No athlete here matches “{search.trim()}”.
                </td>
              </tr>
            )}
            {shown.map((row) => (
              <tr key={row.id}>
                <td style={{ padding: '12px', borderBottom: `1px solid ${HAIR}`, color: INK, fontSize: 15 }}>
                  {row.athlete_name || 'Athlete'}
                  {row.bib ? <span style={{ ...mono, color: MUTED, fontSize: 12, marginLeft: 8 }}>Bib {row.bib}</span> : null}
                </td>
                <td style={{ ...mono, padding: '12px', textAlign: 'right', borderBottom: `1px solid ${HAIR}`, color: '#3B2A14' }}>{formatKm(row.distance_meters)}</td>
                <td style={{ ...mono, padding: '12px', textAlign: 'right', borderBottom: `1px solid ${HAIR}`, fontWeight: 600, color: INK }}>{formatClock(row.duration_seconds)}</td>
                <td style={{ padding: '12px', textAlign: 'right', borderBottom: `1px solid ${HAIR}`, fontSize: 12, color: MUTED }}>{row.status}</td>
                <td style={{ padding: '12px', textAlign: 'right', borderBottom: `1px solid ${HAIR}` }}>
                  <label htmlFor={`place-${row.id}`} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
                    Podium place for {row.athlete_name || 'this athlete'}
                  </label>
                  <select
                    id={`place-${row.id}`}
                    value={row.declared_position ?? ''}
                    disabled={busy === `mark-${row.id}`}
                    onChange={(e) => mark(row.id, e.target.value)}
                    style={{ minHeight: 40, minWidth: 92, padding: '0 8px', borderRadius: 9, border: `1px solid ${HAIR}`, fontSize: 14, color: INK, background: '#fff' }}
                  >
                    <option value="">—</option>
                    {Array.from({ length: PODIUM_PLACES }, (_, i) => i + 1).map((place) => (
                      <option key={place} value={place}>{ordinal(place)}</option>
                    ))}
                  </select>
                </td>

                {/* ── REMOVE, WITHDRAWN BEFORE IT SHIPPED — 2026-11-03 ──────
                    Built to answer "I attached the wrong run". It worked on
                    screen and is wrong underneath, so it is commented out
                    rather than left live.

                    WHY. `event_result.soft_delete` is TRUE, so the delete sets
                    `deleted_at` and leaves the row in place. The table's unique
                    rule on `registration_id` is a PLAIN constraint, which still
                    counts deleted rows. So a removed result goes on occupying
                    its registration for ever, and that athlete becomes:

                      · offered again by the attach panel (its query filters
                        `deleted_at IS NULL`, so the row looks gone),
                      · impossible to actually attach — the insert is refused on
                        the unique constraint, SILENTLY, inside a batch,
                      · poison to the CSV import, whose batch is one
                        transaction: one such athlete takes the whole upload
                        down. Measured: 1,400 of 10,000 rows saved, then refused.

                    MEASURED, not reasoned: after removing Kabir_patel_507, a
                    500-run attach ran to completion and registration 9500 still
                    held only the soft-deleted row. No error reached the screen.

                    There is no frontend way out. Every read available here —
                    the public table read and `organizer_race_results` alike —
                    excludes soft-deleted rows, so the row cannot be found and
                    revived, and its id cannot be learned in order to update it.

                    BOTH FIXED, 2026-11-03, and the button is live again:

                    1. `event_result`'s plain UNIQUE (registration_id) is now a
                       CONDITIONAL index — unique among rows with `deleted_at IS
                       NULL` — shipped as pack 1.0.12. Soft delete is kept, so
                       the audit trail survives, and a removed result stops
                       owning its registration.
                    2. The guard now gates on the race's `results_published`
                       flag as well as the award rows. Award rows outlive an
                       Unpublish on purpose (deleting them once made
                       republishing impossible), so guarding on their existence
                       alone blocked Remove on a race whose podium was down. */}
                <td style={{ padding: '12px', textAlign: 'right', borderBottom: `1px solid ${HAIR}` }}>
                  {publishedResultIds.has(String(row.id)) ? (
                    <span
                      style={{ fontSize: 12, color: MUTED }}
                      title={
                        racePublished.data
                          ? 'A published winner. Clear their place and publish again before removing.'
                          : 'Still named in the award table. Clear their place and publish again before removing.'
                      }
                    >
                      {racePublished.data ? 'Published' : 'Declared'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => (confirming === row.id ? onRemove(row) : setConfirming(row.id))}
                      onBlur={() => setConfirming((c) => (c === row.id ? null : c))}
                      disabled={busy === `remove-${row.id}`}
                      aria-label={
                        confirming === row.id
                          ? `Confirm removing the result for ${row.athlete_name || 'this athlete'}`
                          : `Remove the result for ${row.athlete_name || 'this athlete'}`
                      }
                      style={{
                        minHeight: 36, padding: '0 12px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                        cursor: busy === `remove-${row.id}` ? 'default' : 'pointer',
                        border: `1px solid ${confirming === row.id ? '#B91C1C' : HAIR}`,
                        background: confirming === row.id ? '#B91C1C' : '#fff',
                        color: confirming === row.id ? '#fff' : MUTED,
                      }}
                    >
                      {busy === `remove-${row.id}`
                        ? 'Removing…'
                        : (confirming === row.id ? 'Confirm' : 'Remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
